import {
  EntityReportAttachments,
  EntitySection,
  ReportKind,
  useBusinessEntity,
  useExportBusinessEntityReport,
} from '@commenda/api';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Icons,
} from '@commenda/kakashi';
import { useEffect, useState } from 'react';
import { captureCompanyChartPng } from './exports/captureCompanyCharts';

interface ExportEntityReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessEntityId: number;
  businessEntityName?: string | null;
  onError?: (message: string) => void;
}

// Labels mirror the BE's REPORT_TITLES section headers.
const SECTIONS: { kind: EntitySection; label: string }[] = [
  { kind: 'organization-chart', label: 'Organization Chart' },
  { kind: 'tax-chart', label: 'Tax Chart' },
  { kind: 'business-info', label: 'Business Information' },
  { kind: 'ownership', label: 'Ownership & Control' },
  { kind: 'key-persons', label: 'Key Persons & Roles' },
];

// Every EntitySection is also a valid standalone ReportKind, so a single
// selection drives the per-kind report directly (composite POST short-
// circuits on the BE for chart-only kinds, the other three use their
// existing GET endpoints). Multi-section → composite.
function pickKind(selected: EntitySection[]): { kind: ReportKind; sections?: EntitySection[] } {
  if (selected.length === 1) return { kind: selected[0]! };
  return { kind: 'entity', sections: selected };
}

export const ExportEntityReportModal = ({
  isOpen,
  onClose,
  businessEntityId,
  businessEntityName,
  onError,
}: ExportEntityReportModalProps) => {
  const [selected, setSelected] = useState<EntitySection[]>(() => SECTIONS.map((s) => s.kind));
  const [isCapturing, setIsCapturing] = useState(false);
  const { exportingKind, exportReport } = useExportBusinessEntityReport({
    businessEntityId,
    businessEntityName,
    onError,
  });
  // Chart capture needs the workspace companyId — derived from the entity
  // so callers don't have to thread it through.
  const { data: entity } = useBusinessEntity(businessEntityId);
  const companyId = entity?.generalCorporationInfo.companyId;

  // Modal stays mounted across open/close cycles — visibility just
  // toggles — so reset to "all selected" each open instead of relying
  // on unmount to clear state.
  useEffect(() => {
    if (isOpen) setSelected(SECTIONS.map((s) => s.kind));
  }, [isOpen]);

  const isExporting = exportingKind !== null || isCapturing;

  const allSelected = selected.length === SECTIONS.length;
  const someSelected = selected.length > 0 && !allSelected;

  const toggle = (kind: EntitySection) => {
    setSelected((prev) =>
      prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind],
    );
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : SECTIONS.map((s) => s.kind));
  };

  const handleExport = async () => {
    if (selected.length === 0 || isExporting) return;
    // SECTIONS-ordered, not click-ordered, so the composite's TOC is stable.
    const ordered = SECTIONS.map((s) => s.kind).filter((k) => selected.includes(k));
    const { kind, sections } = pickKind(ordered);

    let attachments: EntityReportAttachments | undefined;
    const needsOrgChart = ordered.includes('organization-chart');
    const needsTaxChart = ordered.includes('tax-chart');
    if ((needsOrgChart || needsTaxChart)) {
      if (!companyId) {
        onError?.('Entity is still loading — please retry in a moment.');
        return;
      }
      setIsCapturing(true);
      try {
        // Captures run sequentially: html-to-image queries .react-flow__viewport,
        // and two simultaneous mounts would race even with scoped containers.
        const orgPng = needsOrgChart
          ? await captureCompanyChartPng({ companyId, kind: 'org' })
          : null;
        const taxPng = needsTaxChart
          ? await captureCompanyChartPng({ companyId, kind: 'tax' })
          : null;
        attachments = {
          ...(orgPng ? { organizationChartPng: orgPng } : {}),
          ...(taxPng ? { taxChartPng: taxPng } : {}),
        };
      } catch (err) {
        setIsCapturing(false);
        onError?.((err as Error).message || 'Failed to capture chart');
        return;
      }
      setIsCapturing(false);
    }

    await exportReport(kind, sections, attachments);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[464px] max-w-[464px] p-0">
        <div className="flex items-start justify-between p-3xl pb-0">
          <DialogTitle className="txt-large-bold text-text-primary">
            Export entity report
          </DialogTitle>
        </div>

        <div className="flex flex-col gap-2xl p-3xl">
          <p className="txt-small-regular text-text-primary">
            Generate a shareable report containing entity information, ownership
            details, and key person records.
          </p>

          <div className="flex flex-col gap-xl">
            {/* `w-fit` keeps the click target on checkbox + label only — not
                the empty row space to the right. */}
            <label className="flex w-fit items-start gap-md cursor-pointer">
              <span className="flex items-center justify-center pt-xxs shrink-0">
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  indeterminate={someSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all sections"
                />
              </span>
              <span className="txt-small-medium text-text-primary">
                Select all
              </span>
            </label>

            {/* Vertical line: top/bottom 2px accounts for the pt-xxs on the
                checkbox wrapper spans, so it lands exactly at the first
                child's top edge and last child's bottom edge. left: 7.5px
                centers the 1px line on x=8 — the Select-all checkbox column. */}
            <div className="relative">
              <div
                aria-hidden
                className="absolute w-px bg-border-primary"
                style={{ left: '7.5px', top: '2px', bottom: '2px' }}
              />
              <div className="flex flex-col gap-xl" style={{ paddingLeft: '24px' }}>
                {SECTIONS.map(({ kind, label }) => (
                  <label
                    key={kind}
                    className="flex w-fit items-start gap-md cursor-pointer"
                  >
                    <span className="flex items-center justify-center pt-xxs shrink-0">
                      <Checkbox
                        checked={selected.includes(kind)}
                        onCheckedChange={() => toggle(kind)}
                        aria-label={label}
                      />
                    </span>
                    <span className="txt-small-medium text-text-primary">
                      {label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <Alert
            variant="info"
            icon={<Icons.InfoCircle className="size-5 text-text-info" />}
            description="Reports are exported as PDF files and include the latest synced entity data."
          />
        </div>

        <div className="flex justify-end gap-lg p-3xl pt-0">
          <Button variant="secondary" size="md" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => void handleExport()}
            isLoading={isExporting}
            loadingLabel={isCapturing ? 'Capturing charts' : 'Exporting report'}
            disabled={selected.length === 0}
          >
            Export report
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
